// ══════════════════════════════════════════════════════════════════════════════
// PriestMonitorPage.js — Priest's silent control panel (second device)
// Access: /?priest_monitor=<booking_id>&secret=<admin_secret>
// Open on a tablet/laptop placed in front of the priest.
// Joins the channel as a silent viewer — no video/audio published.
// Invisible to devotees. Used for Sankalp spotlight + Mute All control.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import AgoraRTC, {
  AgoraRTCProvider,
  RemoteUser,
  useJoin,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";

const monitorClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const C = {
  bg: "#06030A", bg2: "#0D0818", bg3: "#130D22",
  saffron: "#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text: "#EDE0C8", dim: "#8A7558",
  border: "rgba(201,149,58,0.25)",
};

// ── Ritual moment cards — shown on devotee screen in their language ────────────
// Priest taps a moment button → devotee sees a brief native-language card
// Languages: en, hi, te, ta, kn (English, Hindi, Telugu, Tamil, Kannada)
const RITUAL_MOMENTS = [
  {
    id: "invocation",
    emoji: "🙏",
    label: "Invocation",
    text: {
      en: "Inviting the deity to be present — the sacred space is now open",
      hi: "देवता का आह्वान — अब यह स्थान पवित्र हो गया है",
      te: "దేవతను ఆహ్వానిస్తున్నారు — ఈ పవిత్ర స్థలం ఇప్పుడు తెరుచుకుంది",
      ta: "தெய்வத்தை அழைக்கிறோம் — இப்போது இந்த இடம் புனிதமானது",
      kn: "ದೇವರನ್ನು ಆಹ್ವಾನಿಸಲಾಗುತ್ತಿದೆ — ಈ ಪವಿತ್ರ ಸ್ಥಳ ಈಗ ತೆರೆದಿದೆ",
    },
  },
  {
    id: "kalash",
    emoji: "🏺",
    label: "Kalash",
    text: {
      en: "The sacred pot is established — all holy rivers and divine energies are present here now",
      hi: "कलश स्थापित हो गया — सभी पवित्र नदियाँ और देवशक्तियाँ यहाँ उपस्थित हैं",
      te: "కలశం స్థాపించబడింది — అన్ని పవిత్ర నదులు మరియు దివ్యశక్తులు ఇప్పుడు ఇక్కడ ఉన్నాయి",
      ta: "கலசம் அமைக்கப்பட்டது — அனைத்து புனித நதிகளும் தெய்வீக சக்திகளும் இங்கே இருக்கின்றன",
      kn: "ಕಲಶ ಸ್ಥಾಪಿಸಲಾಗಿದೆ — ಎಲ್ಲಾ ಪವಿತ್ರ ನದಿಗಳು ಮತ್ತು ದೈವಶಕ್ತಿಗಳು ಇಲ್ಲಿ ಈಗ ಇವೆ",
    },
  },
  {
    id: "ganesh",
    emoji: "🐘",
    label: "Ganesh Puja",
    text: {
      en: "Ganesha is worshipped first — removing all obstacles before your intention is offered",
      hi: "पहले गणेशजी की पूजा — आपके संकल्प से पहले सभी विघ्न दूर किए जा रहे हैं",
      te: "ముందుగా గణేశుడిని పూజిస్తున్నారు — మీ సంకల్పం అర్పించే ముందు అన్ని విఘ్నాలు తొలగించబడుతున్నాయి",
      ta: "முதலில் கணேஷ் வழிபாடு — உங்கள் நோக்கம் அர்ப்பணிக்கப்படுவதற்கு முன் தடைகள் நீக்கப்படுகின்றன",
      kn: "ಮೊದಲು ಗಣೇಶನ ಪೂಜೆ — ನಿಮ್ಮ ಸಂಕಲ್ಪ ಅರ್ಪಿಸುವ ಮೊದಲು ಎಲ್ಲಾ ವಿಘ್ನಗಳು ನಿವಾರಣೆಯಾಗುತ್ತಿವೆ",
    },
  },
  {
    id: "offerings",
    emoji: "🌸",
    label: "Offerings",
    text: {
      en: "Sacred offerings are being made — flowers, water, incense, light — each one carries your devotion to the deity",
      hi: "पवित्र अर्पण किए जा रहे हैं — फूल, जल, धूप, दीप — हर एक में आपकी भक्ति देवता तक पहुँचती है",
      te: "పవిత్ర నైవేద్యాలు సమర్పించబడుతున్నాయి — పూలు, నీళ్ళు, ధూపం, దీపం — ప్రతిదీ మీ భక్తిని దేవతకు చేర్చుతోంది",
      ta: "புனித காணிக்கைகள் செலுத்தப்படுகின்றன — மலர்கள், நீர், தூபம், விளக்கு — ஒவ்வொன்றும் உங்கள் பக்தியை தெய்வத்திடம் சேர்க்கிறது",
      kn: "ಪವಿತ್ರ ನೈವೇದ್ಯಗಳನ್ನು ಅರ್ಪಿಸಲಾಗುತ್ತಿದೆ — ಹೂವು, ನೀರು, ಧೂಪ, ದೀಪ — ಪ್ರತಿಯೊಂದೂ ನಿಮ್ಮ ಭಕ್ತಿಯನ್ನು ದೇವರಿಗೆ ತಲುಪಿಸುತ್ತದೆ",
    },
  },
  {
    id: "mantra",
    emoji: "📿",
    label: "Main Mantra",
    text: {
      en: "The core mantras are being chanted — your name and intention are woven into every word",
      hi: "मुख्य मंत्र जप हो रहा है — आपका नाम और संकल्प हर शब्द में गूँज रहा है",
      te: "ముఖ్యమైన మంత్రాలు జపించబడుతున్నాయి — ప్రతి మాటలో మీ పేరు మరియు సంకల్పం ప్రతిధ్వనిస్తోంది",
      ta: "முக்கிய மந்திரங்கள் உச்சரிக்கப்படுகின்றன — ஒவ்வொரு வார்த்தையிலும் உங்கள் பெயரும் நோக்கமும் ஒலிக்கின்றன",
      kn: "ಮುಖ್ಯ ಮಂತ್ರಗಳನ್ನು ಜಪಿಸಲಾಗುತ್ತಿದೆ — ಪ್ರತಿ ಪದದಲ್ಲೂ ನಿಮ್ಮ ಹೆಸರು ಮತ್ತು ಸಂಕಲ್ಪ ಅನುರಣಿಸುತ್ತಿದೆ",
    },
  },
  {
    id: "homam",
    emoji: "🔥",
    label: "Homam / Fire",
    text: {
      en: "Offerings are made to the sacred fire — what you give, the fire purifies and carries directly to the divine",
      hi: "पवित्र अग्नि में आहुति दी जा रही है — आप जो अर्पण करते हैं, अग्नि उसे शुद्ध करके सीधे देवता तक पहुँचाती है",
      te: "పవిత్ర అగ్నిలో ఆహుతి అర్పించబడుతోంది — మీరు ఇచ్చేది అగ్ని శుద్ధి చేసి నేరుగా దేవతకు చేరుస్తుంది",
      ta: "புனித நெருப்பில் ஆகுதி செலுத்தப்படுகிறது — நீங்கள் தரும் தியாகத்தை அக்னி தூய்மைப்படுத்தி தெய்வத்திடம் கொண்டு செல்கிறது",
      kn: "ಪವಿತ್ರ ಅಗ್ನಿಗೆ ಆಹುತಿ ನೀಡಲಾಗುತ್ತಿದೆ — ನೀವು ನೀಡುವುದನ್ನು ಅಗ್ನಿ ಶುದ್ಧಿಗೊಳಿಸಿ ನೇರವಾಗಿ ದೇವರಿಗೆ ತಲುಪಿಸುತ್ತದೆ",
    },
  },
  {
    id: "blessing",
    emoji: "✨",
    label: "Final Blessing",
    text: {
      en: "The puja is complete — the deity has received your offering and your sankalpa is now sealed with divine grace",
      hi: "पूजा पूर्ण हुई — देवता ने आपका अर्पण स्वीकार किया और आपका संकल्प दिव्य कृपा से अभिषिक्त हो गया",
      te: "పూజ పూర్తయింది — దేవత మీ నైవేద్యాన్ని స్వీకరించారు మరియు మీ సంకల్పం దివ్యానుగ్రహంతో ముద్రించబడింది",
      ta: "பூஜை நிறைவடைந்தது — தெய்வம் உங்கள் காணிக்கையை ஏற்றுக்கொண்டது, உங்கள் சங்கல்பம் தெய்வீக அருளால் முத்திரையிடப்பட்டது",
      kn: "ಪೂಜೆ ಮುಗಿಯಿತು — ದೇವರು ನಿಮ್ಮ ನೈವೇದ್ಯ ಸ್ವೀಕರಿಸಿದ್ದಾರೆ ಮತ್ತು ನಿಮ್ಮ ಸಂಕಲ್ಪ ದಿವ್ಯ ಕೃಪೆಯಿಂದ ಮುದ್ರಿಸಲ್ಪಟ್ಟಿದೆ",
    },
  },
];

// ── Monitor inner (hooks must be inside Provider) ─────────────────────────────
function MonitorInner({ config }) {
  const { appId, token, channel, uid, priestUid, pujaName, priestName } = config;

  useRTCClient(); // keep hook for agora-rtc-react internal state

  const [spotlightUid, setSpotlightUid] = useState(null);
  const [allMuted,     setAllMuted]     = useState(false);
  const [aarti,        setAarti]        = useState(false);
  const [elapsed,      setElapsed]      = useState(0);
  const [raisedHands,   setRaisedHands]  = useState(new Set()); // uids with hand up
  const [mutedDevotees, setMutedDevotees]= useState(new Set()); // individually muted
  const [dakshinaActive,    setDakshinaActive]    = useState(false);       // dakshina screen sent
  const [dakshinaVisibility,setDakshinaVisibility]= useState("private");   // "private" | "all"
  const [dakshinaPaid,      setDakshinaPaid]      = useState(new Set());   // uids who confirmed
  const [uidNames,          setUidNames]          = useState({});          // uid → { name, device }
  const [focusedUid,        setFocusedUid]        = useState(null);        // devotee shown large on Phone 2
  const [momentsOpen,       setMomentsOpen]       = useState(false);       // ritual moment panel open
  const [lastMomentId,      setLastMomentId]      = useState(null);        // last sent moment (highlight)

  // Join as audience — no publish at all
  const { isLoading } = useJoin({ appid: appId, channel, token, uid }, true);
  const remoteUsers   = useRemoteUsers();

  // Filter out priest's own stream — monitor only cares about devotees
  const devotees = remoteUsers.filter(u => u.uid !== priestUid && u.uid !== uid);

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2,"0")}:${String(s % 60).padStart(2,"0")}`;

  // SDK 4.20+: no createDataStream needed — sendStreamMessage takes data directly

  // Listen for devotee signals (raise_hand)
  useEffect(() => {
    const onMsg = (_, data) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(data));
        if (msg.type === "identify") {
          setUidNames(prev => ({ ...prev, [msg.uid]: { name: msg.name, device: msg.device } }));
        } else if (msg.type === "raise_hand") {
          setRaisedHands(prev => {
            const next = new Set(prev);
            if (msg.raised) next.add(msg.uid);
            else next.delete(msg.uid);
            return next;
          });
        } else if (msg.type === "dakshina_paid") {
          setDakshinaPaid(prev => { const n = new Set(prev); n.add(msg.uid); return n; });
        }
      } catch {}
    };
    monitorClient.on("stream-message", onMsg);
    return () => monitorClient.off("stream-message", onMsg);
  }, []); // eslint-disable-line

  const send = (msg) => {
    try {
      monitorClient.sendStreamMessage(new TextEncoder().encode(JSON.stringify(msg)));
    } catch {}
  };

  const handleSankalp = (devoteeUid, displayName) => {
    if (spotlightUid === devoteeUid) {
      // End sankalp — clear focus and spotlight
      setSpotlightUid(null);
      setFocusedUid(null);
      send({ type: "clear_sankalp" });
    } else {
      setSpotlightUid(devoteeUid);
      setFocusedUid(devoteeUid); // switch Phone 2 to focused view
      send({ type: "sankalp", uid: devoteeUid, name: displayName });
    }
  };

  const handleMuteAll = () => {
    const next = !allMuted;
    setAllMuted(next);
    send({ type: "mute_all", muted: next });
  };

  const handleAarti = () => {
    const next = !aarti;
    setAarti(next);
    send({ type: "aarti", active: next });
    // Aarti = everyone unmuted; ending aarti = mute all again
    if (next && allMuted) {
      setAllMuted(false);
      send({ type: "mute_all", muted: false });
    } else if (!next) {
      setAllMuted(true);
      send({ type: "mute_all", muted: true });
    }
  };

  const handleMuteDevotee = (devoteeUid) => {
    const isMuted = mutedDevotees.has(devoteeUid);
    const next = new Set(mutedDevotees);
    if (isMuted) {
      next.delete(devoteeUid);
      send({ type: "mute_devotee", uid: devoteeUid, muted: false });
    } else {
      next.add(devoteeUid);
      send({ type: "mute_devotee", uid: devoteeUid, muted: true });
      // Clear their hand raise when muting individually
      setRaisedHands(prev => { const n = new Set(prev); n.delete(devoteeUid); return n; });
    }
    setMutedDevotees(next);
  };

  const handleAckHand = (devoteeUid) => {
    setRaisedHands(prev => { const n = new Set(prev); n.delete(devoteeUid); return n; });
    send({ type: "raise_hand_ack", uid: devoteeUid });
    // Unmute them so they can speak
    if (mutedDevotees.has(devoteeUid) || allMuted) {
      const next = new Set(mutedDevotees);
      next.delete(devoteeUid);
      setMutedDevotees(next);
      send({ type: "mute_devotee", uid: devoteeUid, muted: false });
    }
    // Switch Phone 2 to focused view so priest can look at them
    setFocusedUid(devoteeUid);
  };

  const handleDakshina = () => {
    const next = !dakshinaActive;
    setDakshinaActive(next);
    send({ type: "dakshina", active: next, visible: dakshinaVisibility });
    if (next) {
      setAllMuted(false);
      send({ type: "mute_all", muted: false });
    }
  };

  const handleMoment = (momentId) => {
    send({ type: "ritual_moment", momentId });
    setLastMomentId(momentId);
    // Auto-clear highlight after 8s (matches devotee card duration)
    setTimeout(() => setLastMomentId(prev => prev === momentId ? null : prev), 8000);
  };

  const cols = devotees.length <= 2 ? devotees.length || 1
             : devotees.length <= 6 ? 3
             : 4;

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes sankalp-ring {
          0%,100% { box-shadow: 0 0 0 2px #C9953A, 0 0 20px rgba(201,149,58,0.5); }
          50%      { box-shadow: 0 0 0 3px #E8832A, 0 0 36px rgba(232,131,42,0.7); }
        }
        @keyframes hand-pulse {
          0%,100% { transform: scale(1);    opacity: 1; }
          50%      { transform: scale(1.15); opacity: 0.8; }
        }
        @keyframes moment-sent {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 24px",
        background: "rgba(13,8,24,0.98)", borderBottom: `1px solid ${C.border}`,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <span style={{ fontSize: "1.4rem", filter: "drop-shadow(0 0 8px rgba(232,131,42,0.5))" }}>🔱</span>
          <div>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.9rem", color: C.saffron }}>
              {pujaName}
            </div>
            <div style={{ fontSize: "0.7rem", color: C.dim, marginTop: "1px" }}>
              Priest Monitor · {priestName || "Priest"} · {fmt(elapsed)}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* 🔴 TALKING NOW — mute all devotees during chanting */}
          <button onClick={handleMuteAll} style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: allMuted ? "rgba(239,68,68,0.2)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${allMuted ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.12)"}`,
            borderRadius: "10px", padding: "9px 20px",
            color: allMuted ? "#fca5a5" : C.dim,
            fontFamily: "'Crimson Pro', serif", fontSize: "0.95rem",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            🔴 {allMuted ? "End Talk" : "Talking Now"}
          </button>

          {/* 🪔 AARTI TIME — grid layout + unmute everyone */}
          <button onClick={handleAarti} style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: aarti ? "rgba(232,131,42,0.25)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${aarti ? "rgba(232,131,42,0.7)" : "rgba(255,255,255,0.12)"}`,
            borderRadius: "10px", padding: "9px 20px",
            color: aarti ? C.saffron : C.dim,
            fontFamily: "'Crimson Pro', serif", fontSize: "0.95rem",
            cursor: "pointer", transition: "all 0.2s",
            fontWeight: aarti ? "bold" : "normal",
          }}>
            🪔 {aarti ? "End Aarti" : "Aarti Time"}
          </button>

          {/* 💰 DAKSHINA — visibility toggle + send */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {/* Visibility mode chips — only when not yet active */}
            {!dakshinaActive && (
              <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
                {[
                  { key: "private", label: "🔒", title: "Private — only priest sees who paid" },
                  { key: "all",     label: "👥", title: "Group — all devotees see when someone pays" },
                ].map(opt => (
                  <button key={opt.key} onClick={() => setDakshinaVisibility(opt.key)} title={opt.title} style={{
                    padding: "9px 13px",
                    background: dakshinaVisibility === opt.key ? "rgba(201,149,58,0.2)" : "transparent",
                    border: "none", borderRight: opt.key === "private" ? "1px solid rgba(255,255,255,0.1)" : "none",
                    color: dakshinaVisibility === opt.key ? C.gold : C.dim,
                    fontSize: "0.9rem", cursor: "pointer", transition: "all 0.15s",
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <button onClick={handleDakshina} style={{
              display: "flex", alignItems: "center", gap: "8px",
              background: dakshinaActive ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${dakshinaActive ? "rgba(34,197,94,0.45)" : "rgba(255,255,255,0.12)"}`,
              borderRadius: "10px", padding: "9px 20px",
              color: dakshinaActive ? "#86efac" : C.dim,
              fontFamily: "'Crimson Pro', serif", fontSize: "0.95rem",
              cursor: "pointer", transition: "all 0.2s",
            }}>
              💰 {dakshinaActive
                ? "End Dakshina"
                : dakshinaVisibility === "all" ? "Dakshina (Group)" : "Dakshina"}
            </button>
          </div>

          {/* 📖 MOMENTS — ritual moment cards for devotees */}
          <button onClick={() => setMomentsOpen(v => !v)} style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: momentsOpen ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${momentsOpen ? "rgba(99,102,241,0.5)" : "rgba(255,255,255,0.12)"}`,
            borderRadius: "10px", padding: "9px 20px",
            color: momentsOpen ? "#a5b4fc" : C.dim,
            fontFamily: "'Crimson Pro', serif", fontSize: "0.95rem",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            📖 {momentsOpen ? "Close" : "Moments"}
          </button>

          <div style={{
            background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.28)",
            borderRadius: "20px", padding: "6px 14px",
            fontSize: "0.72rem", color: "#86efac",
            display: "flex", alignItems: "center", gap: "6px",
          }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
            {isLoading ? "Connecting…" : `${devotees.length} devotee${devotees.length !== 1 ? "s" : ""} live`}
          </div>
        </div>
      </div>

      {/* ── Ritual Moments panel — slides in below top bar ───────────────────── */}
      {momentsOpen && (
        <div style={{
          background: "rgba(13,8,24,0.98)", borderBottom: `1px solid ${C.border}`,
          padding: "14px 24px", flexShrink: 0,
        }}>
          <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "10px", letterSpacing: "0.05em" }}>
            TAP A MOMENT → DEVOTEES SEE A CARD IN THEIR LANGUAGE
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {RITUAL_MOMENTS.map(m => (
              <button
                key={m.id}
                onClick={() => handleMoment(m.id)}
                style={{
                  display: "flex", alignItems: "center", gap: "7px",
                  background: lastMomentId === m.id
                    ? "rgba(99,102,241,0.25)"
                    : "rgba(255,255,255,0.04)",
                  border: `1px solid ${lastMomentId === m.id ? "rgba(99,102,241,0.6)" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: "10px", padding: "8px 16px",
                  color: lastMomentId === m.id ? "#a5b4fc" : C.text,
                  fontSize: "0.82rem", cursor: "pointer",
                  transition: "all 0.2s",
                  animation: lastMomentId === m.id ? "moment-sent 0.4s ease-out" : "none",
                  fontFamily: "'Crimson Pro', serif",
                }}>
                <span>{m.emoji}</span>
                <span>{m.label}</span>
                {lastMomentId === m.id && (
                  <span style={{ fontSize: "0.65rem", color: "#818cf8", marginLeft: "2px" }}>✓ sent</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Focused view — shown when priest taps Sankalp or acknowledges hand ── */}
      {focusedUid && (() => {
        const faceCamUser  = devotees.find(u => u.uid === focusedUid);
        const focusedInfo  = uidNames[focusedUid];
        const focusedName  = focusedInfo?.name || "Devotee";
        // Stand cam = device 2 of same family — shows EVERYTHING (face+family+deity)
        const standCam = devotees.find(u =>
          u.uid !== focusedUid &&
          uidNames[u.uid]?.name === focusedName &&
          uidNames[u.uid]?.device === 2
        );
        // Main view: stand cam if available (shows everything), else face cam
        const mainUser    = standCam || faceCamUser;
        const pipUser     = standCam ? faceCamUser : null; // face cam as PIP only when stand cam exists
        const isSpotlit   = spotlightUid === focusedUid;
        return (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "14px", gap: "10px", minHeight: 0 }}>
            {/* Main video — stand cam (comprehensive) or face cam */}
            <div style={{
              flex: 1, borderRadius: "18px", overflow: "hidden",
              background: C.bg2, position: "relative", minHeight: 0,
              border: `2px solid ${isSpotlit ? C.saffron : C.border}`,
              boxShadow: isSpotlit ? "0 0 32px rgba(201,149,58,0.2)" : "none",
              animation: isSpotlit ? "sankalp-ring 2s ease-in-out infinite" : "none",
            }}>
              {mainUser?.hasVideo ? (
                <RemoteUser user={mainUser} playVideo playAudio={false}
                  style={{ width: "100%", height: "100%" }} />
              ) : (
                <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"3rem" }}>🙏</div>
              )}

              {/* Label */}
              <div style={{
                position: "absolute", top: "14px", left: "14px",
                background: "rgba(6,3,10,0.8)", borderRadius: "8px",
                padding: "4px 12px", fontSize: "0.78rem", color: C.cream,
                display: "flex", alignItems: "center", gap: "6px",
              }}>
                {isSpotlit && <span>🙏</span>}
                <span>{focusedName}</span>
                {standCam && <span style={{ fontSize: "0.6rem", color: "#818cf8" }}>· Full scene</span>}
              </div>

              {/* Face cam PIP — only when stand cam is the main view */}
              {pipUser?.hasVideo && (
                <div style={{
                  position: "absolute", bottom: "14px", right: "14px",
                  width: "140px", height: "100px",
                  borderRadius: "10px", overflow: "hidden",
                  border: `1px solid ${C.border}`,
                }}>
                  <RemoteUser user={pipUser} playVideo playAudio={false}
                    style={{ width: "100%", height: "100%" }} />
                  <div style={{
                    position: "absolute", bottom: "4px", left: "6px",
                    fontSize: "0.58rem", color: C.dim,
                  }}>Face cam</div>
                </div>
              )}
            </div>

            {/* Eye contact guide + controls */}
            <div style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", flexShrink: 0,
              background: C.bg2, borderRadius: "12px",
              padding: "10px 16px", border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: "0.75rem", color: C.dim, fontStyle: "italic" }}>
                📱 Hold Phone 2 beside your altar cam — devotee sees your eyes
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {isSpotlit && (
                  <button onClick={() => handleSankalp(focusedUid, focusedName)} style={{
                    background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)",
                    borderRadius: "8px", padding: "6px 14px",
                    color: "#fca5a5", fontSize: "0.78rem", cursor: "pointer",
                  }}>End Sankalp</button>
                )}
                <button onClick={() => setFocusedUid(null)} style={{
                  background: "rgba(255,255,255,0.06)", border: `1px solid ${C.border}`,
                  borderRadius: "8px", padding: "6px 16px",
                  color: C.dim, fontSize: "0.78rem", cursor: "pointer",
                }}>← Grid</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Devotee grid ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: "16px", overflowY: "auto", display: focusedUid ? "none" : "block" }}>
        {devotees.length === 0 ? (
          <div style={{
            height: "100%", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: "16px",
            color: C.dim, fontStyle: "italic", minHeight: "300px",
          }}>
            <div style={{ fontSize: "3rem" }}>🙏</div>
            <div style={{ fontSize: "0.9rem" }}>Waiting for devotees to join…</div>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: "14px",
          }}>
            {devotees.map((u, i) => {
              const isSpotlit  = spotlightUid === u.uid;
              const nameInfo   = uidNames[u.uid];
              const isSecondCam= nameInfo?.device === 2;
              const displayName= nameInfo
                ? (isSecondCam ? `${nameInfo.name} — Cam 2` : nameInfo.name)
                : `Devotee ${i + 1}`;
              return (
                <div key={u.uid} style={{
                  borderRadius: "14px", overflow: "hidden",
                  background: C.bg2,
                  border: `1px solid ${isSpotlit ? C.saffron : isSecondCam ? "rgba(99,102,241,0.4)" : C.border}`,
                  transition: "transform 0.3s, border-color 0.2s",
                  transform: isSpotlit ? "scale(1.03)" : "scale(1)",
                  animation: isSpotlit ? "sankalp-ring 2s ease-in-out infinite" : "none",
                  position: "relative",
                }}>
                  {/* Video tile */}
                  <div style={{ aspectRatio: "16/9", background: C.bg3 }}>
                    {u.hasVideo ? (
                      <RemoteUser
                        user={u}
                        playVideo
                        playAudio={false}
                        style={{ width: "100%", height: "100%" }}
                      />
                    ) : (
                      <div style={{
                        width: "100%", height: "100%",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "2rem",
                      }}>🙏</div>
                    )}
                  </div>

                  {/* Raised hand badge — floats over video */}
                  {raisedHands.has(u.uid) && (
                    <div style={{
                      position: "absolute", top: "10px", left: "10px", zIndex: 3,
                      background: "rgba(201,149,58,0.95)", borderRadius: "8px",
                      padding: "3px 10px", fontSize: "0.7rem", color: "#06030A",
                      fontFamily: "'Cinzel Decorative', serif", letterSpacing: "0.03em",
                      display: "flex", alignItems: "center", gap: "5px",
                      cursor: "pointer",
                      animation: "hand-pulse 1s ease-in-out infinite",
                    }} onClick={() => handleAckHand(u.uid)} title="Tap to unmute & acknowledge">
                      🙋 Wants to speak
                    </div>
                  )}

                  {/* Name + status bar */}
                  <div style={{
                    padding: "8px 12px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "0.82rem", color: C.cream }}>
                        {displayName}
                      </span>
                      {isSecondCam && (
                        <span style={{
                          background: "rgba(99,102,241,0.15)",
                          border: "1px solid rgba(99,102,241,0.35)",
                          borderRadius: "6px", padding: "1px 6px",
                          fontSize: "0.6rem", color: "#818cf8",
                          letterSpacing: "0.04em",
                        }}>📱 Cam 2</span>
                      )}
                      {isSpotlit && (
                        <span style={{
                          background: "rgba(201,149,58,0.2)", border: `1px solid ${C.gold}`,
                          borderRadius: "6px", padding: "1px 7px",
                          fontSize: "0.62rem", color: C.gold,
                          fontFamily: "'Cinzel Decorative', serif",
                        }}>SANKALP</span>
                      )}
                      {dakshinaPaid.has(u.uid) && (
                        <span style={{
                          background: "rgba(34,197,94,0.15)",
                          border: "1px solid rgba(34,197,94,0.4)",
                          borderRadius: "6px", padding: "1px 7px",
                          fontSize: "0.62rem", color: "#86efac",
                        }}>✓ Dakshina</span>
                      )}
                      {(mutedDevotees.has(u.uid) || !u.hasAudio) && (
                        <span style={{ fontSize: "0.75rem" }} title="Muted">🔇</span>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                      {/* Individual mute/unmute button */}
                      <button
                        onClick={() => handleMuteDevotee(u.uid)}
                        title={mutedDevotees.has(u.uid) ? "Unmute this devotee" : "Mute this devotee"}
                        style={{
                          background: mutedDevotees.has(u.uid) ? "rgba(239,68,68,0.15)" : "transparent",
                          border: `1px solid ${mutedDevotees.has(u.uid) ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.1)"}`,
                          borderRadius: "7px", padding: "4px 10px",
                          color: mutedDevotees.has(u.uid) ? "#fca5a5" : C.dim,
                          fontSize: "0.72rem", cursor: "pointer",
                          transition: "all 0.15s", whiteSpace: "nowrap",
                        }}
                      >
                        {mutedDevotees.has(u.uid) ? "🔕 Unmute" : "🔔 Mute"}
                      </button>

                      {/* Sankalp button */}
                      <button
                        onClick={() => handleSankalp(u.uid, displayName)}
                        style={{
                          background: isSpotlit ? "rgba(201,149,58,0.25)" : "transparent",
                          border: `1px solid ${isSpotlit ? C.gold : "rgba(201,149,58,0.3)"}`,
                          borderRadius: "7px", padding: "4px 12px",
                          color: isSpotlit ? C.gold : C.dim,
                          fontSize: "0.75rem", cursor: "pointer",
                          fontFamily: "'Cinzel Decorative', serif",
                          transition: "all 0.15s", whiteSpace: "nowrap",
                        }}
                      >
                        {isSpotlit ? "✓ End" : "Sankalp"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer note ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: "8px 24px",
        borderTop: `1px solid ${C.border}`,
        fontSize: "0.7rem", color: C.dim, textAlign: "center",
        background: "rgba(13,8,24,0.8)",
      }}>
        This device is invisible to devotees · Only you can see this panel
      </div>
    </div>
  );
}

// ── Loading + entry screen ────────────────────────────────────────────────────
export default function PriestMonitorPage({ bookingId, secret }) {
  const [state,  setState]  = useState("loading");
  const [config, setConfig] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!bookingId || !secret) { setState("error"); setErrMsg("Missing booking ID or secret."); return; }
    fetch(`${API}/puja/monitor-token/${bookingId}?secret=${encodeURIComponent(secret)}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) throw new Error(data.detail);
        setConfig({
          appId:      data.app_id,
          token:      data.token,
          channel:    data.channel,
          uid:        data.uid,
          priestUid:  data.priest_uid,
          pujaName:   data.puja_name,
          priestName: data.priest_name,
        });
        setState("ready");
      })
      .catch(e => { setState("error"); setErrMsg(e.message || "Could not load session."); });
  }, [bookingId, secret]);

  if (state === "loading") return (
    <div style={{ minHeight:"100vh", background: C.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:"3rem", color: C.saffron, marginBottom:"12px" }}>ॐ</div>
        <div style={{ color: C.dim, fontSize:"0.85rem", fontStyle:"italic" }}>Loading monitor…</div>
      </div>
    </div>
  );

  if (state === "error") return (
    <div style={{ minHeight:"100vh", background: C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
      <div style={{
        background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)",
        borderRadius:"12px", padding:"24px", maxWidth:"360px", textAlign:"center",
        fontSize:"0.9rem", color:"#fca5a5",
      }}>{errMsg}</div>
    </div>
  );

  if (state === "ready") return (
    <div style={{ minHeight:"100vh", background: C.bg, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px" }}>
      <div style={{ maxWidth:"400px", width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:"2.8rem", marginBottom:"16px" }}>📺</div>
        <div style={{ fontFamily:"'Cinzel Decorative', serif", fontSize:"1rem", color: C.saffron, marginBottom:"8px" }}>
          Priest Monitor
        </div>
        <div style={{ fontSize:"0.85rem", color: C.dim, marginBottom:"6px" }}>{config.pujaName}</div>
        <div style={{
          background: C.bg2, border:`1px solid ${C.border}`,
          borderRadius:"12px", padding:"16px 20px", marginBottom:"24px",
          fontSize:"0.8rem", color: C.dim, textAlign:"left", lineHeight:"1.8",
        }}>
          <div style={{ color: C.saffron, fontFamily:"'Cinzel Decorative', serif", fontSize:"0.68rem", marginBottom:"8px", letterSpacing:"0.05em" }}>
            HOW THIS WORKS
          </div>
          ✓ Place this device (tablet/laptop) in front of you<br />
          ✓ You will see all devotees in a grid<br />
          ✓ Tap "Sankalp" to spotlight a devotee — they glow for everyone<br />
          ✓ Tap "🔴 Talking Now" during chanting to mute all backgrounds<br />
          ✓ Tap "🪔 Aarti Time" to switch all screens to collective aarti view<br />
          ✓ Tap "💰 Dakshina" after final blessing — devotees see payment screen<br />
          ✓ Devotees cannot see this screen — you are invisible
        </div>
        <button
          onClick={() => setState("live")}
          style={{
            width:"100%", padding:"13px",
            background:"linear-gradient(135deg, #E8832A, #C9953A)",
            border:"none", borderRadius:"12px", color:"white",
            fontFamily:"'Cinzel Decorative', serif", fontSize:"0.9rem",
            cursor:"pointer", letterSpacing:"0.04em",
            boxShadow:"0 6px 24px rgba(232,131,42,0.3)",
          }}
        >
          Open Monitor
        </button>
      </div>
    </div>
  );

  return (
    <AgoraRTCProvider client={monitorClient}>
      <MonitorInner config={config} />
    </AgoraRTCProvider>
  );
}
